import React from 'react'
import { StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, Icon, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { useMyOrganizerRequest } from '@hooks'

/**
 * "Vous organisez des événements ?" — shown in Menu → Événements to accounts
 * that are not organizers yet: ask, wait, or see why the request was refused.
 */
export const OrganizerRequestBlock: React.FC<{ onRequest: () => void }> = ({ onRequest }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const { data, isLoading } = useMyOrganizerRequest()
  if (isLoading) return null
  const request = data?.request ?? null

  if (request?.status === 'pending') {
    return (
      <View style={[styles.card, styles.pending]} testID="organizer-request-pending">
        <Icon name="clock" size={24} color={theme.colors.primary} />
        <View style={styles.body}>
          <Text type="subtitleBold">{t('admin.request.pendingTitle')}</Text>
          <Text type="bodySmall" color={theme.colors.greyBlue}>
            {t('admin.request.pendingHint', { name: request.organizationName })}
          </Text>
        </View>
      </View>
    )
  }

  return (
    <View style={styles.card} testID="organizer-request-block">
      <View style={styles.header}>
        <Icon name="calendarStar" size={24} color={theme.colors.primary} />
        <View style={styles.body}>
          <Text type="subtitleBold">{t('admin.request.blockTitle')}</Text>
          <Text type="bodySmall" color={theme.colors.greyBlue}>
            {t('admin.request.blockHint')}
          </Text>
        </View>
      </View>
      {request?.status === 'rejected' ? (
        <View style={styles.rejected} testID="organizer-request-rejected">
          <Text type="bodySmallBold" color={theme.colors.error}>
            {t('admin.request.rejected', { reason: request.rejectionReason ?? '' })}
          </Text>
        </View>
      ) : null}
      <Button
        title={request?.status === 'rejected' ? t('admin.request.retry') : t('admin.request.cta')}
        onPress={onRequest}
      />
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    pending: { flexDirection: 'row', alignItems: 'center' },
    header: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
    body: { flex: 1, minWidth: 0, gap: theme.spacing.xs },
    rejected: {
      padding: theme.spacing.md,
      borderRadius: theme.border.medium,
      borderWidth: 1,
      borderColor: theme.colors.error,
    },
  })
